'use client';

import type { ArchitectureEvent, ArchitectureState } from '@/lib/architecture-state';

const KIND_LABEL: Record<ArchitectureEvent['kind'], string> = {
  'cron-start': 'CRON-START',
  'cron-ok': 'CRON-OK',
  'bp-detect': 'BP-DETECT',
  'bp-fix-run': 'BP-FIX-RUN',
  'bp-fix-verified': 'BP-FIX-OK',
  'bp-fix-failed': 'BP-FIX-FAIL',
  'bp-escalate': 'BP-ESCALATE',
  'drift': 'DRIFT',
  'drift-warn': 'DRIFT-WARN',
  'drift-crit': 'DRIFT-CRIT',
  imessage: 'IMSG',
};

const SEVERITY_TONE: Record<ArchitectureEvent['severity'], string> = {
  ok: 'border-emerald-400/40 text-emerald-300',
  warn: 'border-amber-300/40 text-amber-300',
  fail: 'border-red-400/40 text-red-300',
  info: 'border-zinc-700 text-white/60',
};

function formatTs(iso: string): string {
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return iso;
  const wd = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'][d.getUTCDay()];
  const hh = String(d.getUTCHours()).padStart(2, '0');
  const mm = String(d.getUTCMinutes()).padStart(2, '0');
  return `${wd} ${hh}:${mm}`;
}

interface Props {
  events: ArchitectureState['recentEvents'];
}

export function RecentEvents({ events }: Props): React.JSX.Element {
  if (events.length === 0) {
    return (
      <div className="rounded-md border border-zinc-800 bg-[#0a0e14] p-4">
        <div className="text-[12px] uppercase tracking-wider text-[#c9a84c]">Recent Events</div>
        <div className="mt-3 text-[11px] text-white/40">No events in 5-day window.</div>
      </div>
    );
  }

  return (
    <div className="rounded-md border border-zinc-800 bg-[#0a0e14] p-4">
      <div className="flex items-baseline justify-between">
        <div className="text-[12px] uppercase tracking-wider text-[#c9a84c]">Recent Events</div>
        <div className="text-[10px] text-white/40">
          {events.length} events · last 5 days · newest first
        </div>
      </div>

      <div className="mt-3 max-h-[420px] overflow-y-auto">
        <table className="w-full text-[11px]">
          <thead>
            <tr className="text-[9px] uppercase tracking-wider text-white/40 sticky top-0 bg-[#0a0e14]">
              <th className="text-left font-normal pb-2 pr-3 w-[110px]">When</th>
              <th className="text-left font-normal pb-2 pr-3 w-[110px]">Kind</th>
              <th className="text-left font-normal pb-2 pr-1">Detail</th>
            </tr>
          </thead>
          <tbody>
            {events.map((ev, i) => (
              <tr
                key={`${ev.ts}-${i}`}
                className="border-t border-zinc-900 align-top"
              >
                <td className="py-1.5 pr-3 tabular-nums text-white/60">{formatTs(ev.ts)}</td>
                <td className="py-1.5 pr-3">
                  <span
                    className={`inline-block px-1.5 py-0.5 rounded border text-[9px] uppercase tracking-wider ${SEVERITY_TONE[ev.severity]}`}
                  >
                    {KIND_LABEL[ev.kind] ?? ev.kind}
                  </span>
                </td>
                <td className="py-1.5 pr-1 text-white/80">{ev.summary}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
